import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoLockClosedOutline } from "react-icons/io5";
import "./LookbackPeriodControl.css";
import {
  buildCalendarCells,
  computeDueDatePickerFixedStyle,
  formatIsoDate,
  parseIsoDateParts,
} from "./calendarUtils";
import {
  clampLookbackDays,
  computeLookbackDaysFromStartDate,
  computeStartDateFromLookbackDays,
  DEFAULT_LOOKBACK_QUICK_PRESETS,
  DEFAULT_MIN_LOOKBACK_DAYS,
  formatLookbackStartDateLabel,
  TRIAL_LOOKBACK_LOCKED_REASON,
} from "./lookbackPeriod";

const CUSTOM_LOOKBACK_VALUE = "selected-date";

const formatPresetLabel = (days) => {
  if (days === 1) return "1 day";
  return `${days} days`;
};

/**
 * Whether the lookback day count matches a quick preset exactly.
 *
 * @example
 * matchesLookbackPreset(7, [1, 3, 7, 14]);
 * // true
 */
const matchesLookbackPreset = (days, presetValues) =>
  presetValues.some((preset) => preset === days);

/**
 * Quick-preset + compact portaled calendar for connector/workflow lookback.
 *
 * @example
 * <LookbackPeriodControl value={7} onChange={setLookbackDays} />
 *
 * @example
 * <LookbackPeriodControl
 *   value={7}
 *   onChange={setLookbackDays}
 *   maxDays={7}
 *   lockedPresets={[{ value: 14, lockedReason: "Free trial imports the last 7 days, once." }]}
 * />
 */
const LookbackPeriodControl = ({
  value,
  onChange,
  quickPresets = DEFAULT_LOOKBACK_QUICK_PRESETS,
  minDays = DEFAULT_MIN_LOOKBACK_DAYS,
  maxDays,
  lockedPresets = [],
  disabled = false,
  helperText = "",
}) => {
  const normalizedValue = clampLookbackDays(value, { minDays, maxDays });
  const startDate = useMemo(
    () => computeStartDateFromLookbackDays(normalizedValue, new Date(), { minDays }),
    [minDays, normalizedValue]
  );
  const startParts = parseIsoDateParts(startDate);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const [calendarStyle, setCalendarStyle] = useState(null);
  const [presetMenuStyle, setPresetMenuStyle] = useState(null);
  const [viewYear, setViewYear] = useState(startParts?.year || new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(
    startParts?.monthIndex ?? new Date().getMonth()
  );
  const dateTriggerRef = useRef(null);
  const presetTriggerRef = useRef(null);
  const calendarRef = useRef(null);
  const presetMenuRef = useRef(null);

  const presetValues = useMemo(() => {
    const values = (Array.isArray(quickPresets) ? quickPresets : DEFAULT_LOOKBACK_QUICK_PRESETS)
      .map((entry) => clampLookbackDays(entry, { minDays }))
      .filter((entry, index, list) => list.indexOf(entry) === index)
      .sort((a, b) => a - b);
    return values.length ? values : [minDays];
  }, [minDays, quickPresets]);

  const lockedReasonByDays = useMemo(() => {
    const map = new Map();
    (Array.isArray(lockedPresets) ? lockedPresets : []).forEach((entry) => {
      const days = typeof entry === "number" ? entry : Number(entry?.value);
      if (!Number.isFinite(days)) return;
      const reason =
        (typeof entry === "object" && entry?.lockedReason) || TRIAL_LOOKBACK_LOCKED_REASON;
      map.set(days, reason);
    });
    if (Number.isFinite(maxDays)) {
      presetValues.forEach((days) => {
        if (days > maxDays && !map.has(days)) {
          map.set(days, TRIAL_LOOKBACK_LOCKED_REASON);
        }
      });
    }
    return map;
  }, [lockedPresets, maxDays, presetValues]);

  const cutoffStartDate = Number.isFinite(maxDays)
    ? computeStartDateFromLookbackDays(maxDays, new Date(), { minDays })
    : null;

  const [selectionSource, setSelectionSource] = useState(() =>
    matchesLookbackPreset(normalizedValue, presetValues) ? "preset" : "calendar"
  );

  const dropdownValue =
    selectionSource === "calendar" || !matchesLookbackPreset(normalizedValue, presetValues)
      ? CUSTOM_LOOKBACK_VALUE
      : String(normalizedValue);

  const presetDisplayLabel =
    dropdownValue === CUSTOM_LOOKBACK_VALUE
      ? "Selected date"
      : formatPresetLabel(normalizedValue);

  const calendarCells = useMemo(
    () => buildCalendarCells(viewYear, viewMonthIndex),
    [viewMonthIndex, viewYear]
  );

  const todayIso = formatIsoDate(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );

  const updateCalendarPosition = useCallback(() => {
    if (!dateTriggerRef.current) return;
    const nextStyle = computeDueDatePickerFixedStyle(
      dateTriggerRef.current,
      calendarRef.current,
      { placement: "auto", align: "start" }
    );
    setCalendarStyle(nextStyle);
  }, []);

  const updatePresetMenuPosition = useCallback(() => {
    if (!presetTriggerRef.current) return;
    const nextStyle = computeDueDatePickerFixedStyle(
      presetTriggerRef.current,
      presetMenuRef.current,
      { placement: "auto", align: "start" }
    );
    setPresetMenuStyle(nextStyle);
  }, []);

  useEffect(() => {
    if (!calendarOpen) return undefined;
    const parts = parseIsoDateParts(startDate);
    if (parts) {
      setViewYear(parts.year);
      setViewMonthIndex(parts.monthIndex);
    }
  }, [calendarOpen, startDate]);

  useLayoutEffect(() => {
    if (!calendarOpen) return undefined;
    updateCalendarPosition();
    const pickerEl = calendarRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && pickerEl
        ? new ResizeObserver(() => updateCalendarPosition())
        : null;
    resizeObserver?.observe(pickerEl);
    window.addEventListener("resize", updateCalendarPosition);
    window.addEventListener("scroll", updateCalendarPosition, true);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateCalendarPosition);
      window.removeEventListener("scroll", updateCalendarPosition, true);
    };
  }, [calendarOpen, updateCalendarPosition, viewMonthIndex, viewYear]);

  useLayoutEffect(() => {
    if (!presetMenuOpen) return undefined;
    updatePresetMenuPosition();
    const menuEl = presetMenuRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && menuEl
        ? new ResizeObserver(() => updatePresetMenuPosition())
        : null;
    resizeObserver?.observe(menuEl);
    window.addEventListener("resize", updatePresetMenuPosition);
    window.addEventListener("scroll", updatePresetMenuPosition, true);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePresetMenuPosition);
      window.removeEventListener("scroll", updatePresetMenuPosition, true);
    };
  }, [presetMenuOpen, updatePresetMenuPosition]);

  useEffect(() => {
    if (!calendarOpen && !presetMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (dateTriggerRef.current?.contains(target)) return;
      if (presetTriggerRef.current?.contains(target)) return;
      if (calendarRef.current?.contains(target)) return;
      if (presetMenuRef.current?.contains(target)) return;
      setCalendarOpen(false);
      setPresetMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [calendarOpen, presetMenuOpen]);

  const handlePresetSelect = (days) => {
    if (disabled || lockedReasonByDays.has(days)) return;
    setSelectionSource("preset");
    onChange(clampLookbackDays(days, { minDays, maxDays }));
    setCalendarOpen(false);
    setPresetMenuOpen(false);
  };

  const handleDateSelect = (year, monthIndex, day) => {
    if (disabled) return;
    const isoDate = formatIsoDate(year, monthIndex, day);
    if (cutoffStartDate && isoDate < cutoffStartDate) return;
    const nextDays = computeLookbackDaysFromStartDate(isoDate, new Date(), { minDays });
    if (!nextDays) return;
    setSelectionSource("calendar");
    onChange(clampLookbackDays(nextDays, { minDays, maxDays }));
    setCalendarOpen(false);
    setPresetMenuOpen(false);
  };

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonthIndex + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonthIndex(next.getMonth());
  };

  const toggleCalendar = () => {
    if (disabled) return;
    setPresetMenuOpen(false);
    setCalendarOpen((open) => !open);
  };

  const togglePresetMenu = () => {
    if (disabled) return;
    setCalendarOpen(false);
    setPresetMenuOpen((open) => !open);
  };

  const calendarNode = calendarOpen ? (
    <div
      ref={calendarRef}
      className={`lookback-period-control__calendar lookback-period-control__calendar--portaled${
        calendarStyle?.openAbove ? " lookback-period-control__calendar--above" : ""
      }`}
      role="dialog"
      aria-label="Choose sync start date"
      style={{
        position: "fixed",
        top: calendarStyle?.top ?? -9999,
        left: calendarStyle?.left ?? -9999,
        visibility: calendarStyle ? "visible" : "hidden",
        zIndex: 10000,
      }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="lookback-period-control__calendar-header">
        <button
          type="button"
          className="lookback-period-control__nav-btn"
          onClick={() => shiftMonth(-1)}
          disabled={disabled}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="lookback-period-control__month-label">
          {new Date(viewYear, viewMonthIndex, 1).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          })}
        </span>
        <button
          type="button"
          className="lookback-period-control__nav-btn"
          onClick={() => shiftMonth(1)}
          disabled={disabled}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="lookback-period-control__weekdays" aria-hidden="true">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div className="lookback-period-control__grid" role="grid">
        {calendarCells.map((cell) => {
          const isoDate = formatIsoDate(cell.year, cell.monthIndex, cell.day);
          const isFuture = isoDate > todayIso;
          const isStart = isoDate === startDate;
          const isInRange = isoDate >= startDate && isoDate <= todayIso && !isFuture;
          const isBeforeCutoff = Boolean(cutoffStartDate && isoDate < cutoffStartDate);
          const lockedReason = isBeforeCutoff ? TRIAL_LOOKBACK_LOCKED_REASON : "";
          return (
            <button
              key={isoDate}
              type="button"
              role="gridcell"
              className={[
                "lookback-period-control__day",
                cell.inMonth ? "" : "lookback-period-control__day--muted",
                isInRange ? " lookback-period-control__day--in-range" : "",
                isStart ? " lookback-period-control__day--selected" : "",
                isBeforeCutoff ? " lookback-period-control__day--locked" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleDateSelect(cell.year, cell.monthIndex, cell.day)}
              disabled={disabled || isFuture || isBeforeCutoff}
              title={lockedReason || undefined}
              aria-selected={isStart}
              aria-label={lockedReason ? `${isoDate}. ${lockedReason}` : isoDate}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const presetMenuNode = presetMenuOpen ? (
    <div
      ref={presetMenuRef}
      className={`lookback-period-control__preset-menu lookback-period-control__preset-menu--portaled${
        presetMenuStyle?.openAbove ? " lookback-period-control__preset-menu--above" : ""
      }`}
      role="listbox"
      aria-label="Lookback period preset"
      style={{
        position: "fixed",
        top: presetMenuStyle?.top ?? -9999,
        left: presetMenuStyle?.left ?? -9999,
        visibility: presetMenuStyle ? "visible" : "hidden",
        zIndex: 10000,
      }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {presetValues.map((days) => {
        const selected = dropdownValue === String(days);
        const lockedReason = lockedReasonByDays.get(days);
        return (
          <div
            key={days}
            role="option"
            className={`lookback-period-control__preset-option${
              selected ? " lookback-period-control__preset-option--selected" : ""
            }${lockedReason ? " lookback-period-control__preset-option--locked" : ""}`}
            onClick={() => handlePresetSelect(days)}
            aria-selected={selected}
            aria-disabled={Boolean(lockedReason) || disabled}
          >
            <span className="lookback-period-control__preset-label">
              {formatPresetLabel(days)}
            </span>
            {lockedReason ? (
              <>
                <span className="lookback-period-control__preset-lock">
                  <IoLockClosedOutline aria-hidden="true" />
                </span>
                <span className="lookback-period-control__preset-reason">{lockedReason}</span>
              </>
            ) : null}
          </div>
        );
      })}
      {dropdownValue === CUSTOM_LOOKBACK_VALUE ? (
        <div
          className="lookback-period-control__preset-option lookback-period-control__preset-option--selected lookback-period-control__preset-option--static"
          role="option"
          aria-selected="true"
        >
          Selected date
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="lookback-period-control">
      <p
        className="lookback-period-control__sentence"
        role="group"
        aria-label="Lookback period"
      >
        <span className="lookback-period-control__phrase">
          <span className="lookback-period-control__sentence-text">Sync the last</span>
          <button
            ref={presetTriggerRef}
            type="button"
            className={`lookback-period-control__inline-choice lookback-period-control__inline-choice--preset${
              presetMenuOpen ? " lookback-period-control__inline-choice--open" : ""
            }`}
            onClick={togglePresetMenu}
            disabled={disabled}
            aria-expanded={presetMenuOpen}
            aria-haspopup="listbox"
          >
            {presetDisplayLabel}
            <span className="lookback-period-control__inline-caret" aria-hidden="true">
              ▾
            </span>
          </button>
          <span className="lookback-period-control__sentence-text lookback-period-control__sentence-text--tight">
            , from
          </span>
        </span>
        <button
          ref={dateTriggerRef}
          type="button"
          className={`lookback-period-control__inline-choice lookback-period-control__inline-choice--date${
            calendarOpen ? " lookback-period-control__inline-choice--open" : ""
          }`}
          onClick={toggleCalendar}
          disabled={disabled}
          aria-expanded={calendarOpen}
          aria-haspopup="dialog"
          aria-label="Choose sync start date"
        >
          {formatLookbackStartDateLabel(startDate)}
        </button>
        <span className="lookback-period-control__sentence-text">through today.</span>
      </p>

      {calendarNode && typeof document !== "undefined"
        ? createPortal(calendarNode, document.body)
        : null}

      {presetMenuNode && typeof document !== "undefined"
        ? createPortal(presetMenuNode, document.body)
        : null}

      {helperText ? (
        <p className="lookback-period-control__helper">{helperText}</p>
      ) : null}
    </div>
  );
};

export default LookbackPeriodControl;
